import { EnvironmentOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Modal, Pagination } from 'antd'
import { ModalProps } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'

import {
  useTreatmentServiceGetActiveList,
  useTreatmentServiceGetActiveListKey,
  useTreatmentServiceGetEpidemiologyCaseListEndpointKey,
  useTreatmentServiceSynchroniseEpidemiologyCaseEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { Text, SaveConfirm } from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'

type Props = {
  onCancel: () => void
} & ModalProps

export const NewCasesModal = ({ onCancel, ...props }: Props) => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()
  const palette = useTheme()
  const { userData } = useUser()

  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    institutionId: userData?.currentLegalEntityId
  }

  const { data } = useTreatmentServiceGetActiveList(queryParams, [queryParams])

  const { mutate } = useTreatmentServiceSynchroniseEpidemiologyCaseEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetActiveListKey]
      })

      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetEpidemiologyCaseListEndpointKey]
      })
    }
  })

  const onAdd = (id?: string) => mutate({ id: String(id) })

  return (
    <Modal
      centered
      open
      footer={null}
      maskClosable={false}
      title={t('cases.new_cases')}
      onCancel={onCancel}
      width={920}
      {...props}
    >
      <Flex vertical gap={8}>
        {data?.data?.map((item) => (
          <Flex
            key={item.id}
            justify="space-between"
            align="center"
            className={styles.card}
            gap={16}
          >
            <Flex vertical gap={8}>
              <Flex
                align="center"
                gap={8}
                style={{ color: palette.grey.grey1 }}
              >
                <Text color={palette.grey.grey1}>
                  {dayjs
                    .utc(item.createdAt)
                    .tz()
                    .format(dayMonthYearHourMinutesFormatSeparator)}{' '}
                </Text>
                <Divider className={styles.verticalDivider} type="vertical" />
                <EnvironmentOutlined />
                <Text>{item.institution?.name}</Text>
              </Flex>

              <Text weight={400}>
                {t('epidemiology.case_description', {
                  gender: item.patient?.gender === 'male' ? 'M' : 'F',
                  location:
                    item.patient?.cuatmLocality?.translation?.[
                      currentLang.value
                    ],
                  simeCode: item.patient?.simeCode,
                  age: item.patient?.age
                })}
              </Text>
            </Flex>

            <SaveConfirm
              title={`${t('cases.add_case')}?`}
              onConfirm={() => onAdd(item.id)}
            >
              <Button type="primary">{t('cases.add_case')}</Button>
            </SaveConfirm>
          </Flex>
        ))}

        <Pagination {...pagination} total={data?.totalCount} />
      </Flex>
    </Modal>
  )
}
